import React, { useState, useEffect } from 'react';
import { Form } from 'react-final-form';
import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { EditableUserData, RegistrationField } from '../../redux/types';
import FormGroup from './FormGroup';
import registerFormConfig from '../register/registerFormConfig';
import ReactFinalFormWrapper from '../register/ReactFinalFormWrapper';
import { ReactFinalFormCheckBox } from '../register/ReactFinalFormFields';
import { setInitialUserDataForReactFinalForm } from '../utilities/UserUtility';

type Props = {
  userDetails: EditableUserData;
  disabled?: boolean;
  heading?: React.ReactNode;
  onDoSave?: (key: Record<string, any>) => void;
  onClose?: () => void;
  locale?: string;
  lockCountry?: boolean;
};

const UserFormGroup: React.FC<Props> = ({
  userDetails,
  heading,
  onDoSave = () => {
    // If the form is disabled, we have to pass in a save function
    // due to react-final-form limitation
    // and it can be an empty function like this
  },
  onClose,
  disabled,
  locale,
  lockCountry,
}: Props) => {
  const [initialUserData, setInitialUserData] = useState(
    setInitialUserDataForReactFinalForm(userDetails),
  );

  useEffect(() => {
    if (!userDetails) {
      return;
    }
    setInitialUserData(setInitialUserDataForReactFinalForm(userDetails));
  }, [userDetails]);

  const userFormConfiguration = registerFormConfig(locale, lockCountry)[0];
  const { fields } = userFormConfiguration;
  // disable all fields
  const modifiedFields = (fields as RegistrationField[])
    .filter((field) => field.key !== 'userPassword'
      && field.key !== 'userPasswordConfirm'
      && field.key !== 'userEmail')
    .map((field) => {
      const newFields = { ...field, disable: disabled };
      return newFields;
    });
  const newUserFormConfig = { ...userFormConfiguration, fields: modifiedFields };
  return (
    <FormGroup
      heading={heading}
    >
      <Form
        onSubmit={onDoSave}
        initialValues={initialUserData}
        render={({ form, values }) => {
          const containsError = Object.keys(form.getState().errors || {}).length > 0;
          return (
            <div>
              <form className="flex flex-wrap">
                <ReactFinalFormWrapper registrationForm={newUserFormConfig} />
                <div className="basis-100">
                  <ReactFinalFormCheckBox
                    label={<FormattedMessage id="userDetails.notificationOptOut" />}
                    keyValue="notificationOptOut"
                    key="notificationOptOut"
                    disable={disabled}
                  />
                </div>
                <div className="basis-100">
                  <ReactFinalFormCheckBox
                    label={<FormattedMessage id="registration.newsletterOptIn" />}
                    keyValue="newsletterOptIn"
                    key="newsletterOptIn"
                    disable={disabled}
                  />
                </div>
              </form>
              {
                !disabled && (
                  <div className="mt4">
                    <Button
                      variant="contained"
                      id="UserEditDialogControls-saveButton"
                      onClick={() => onDoSave(values)}
                      disabled={containsError}
                      color="primary"
                    >
                      <FormattedMessage id="save" />
                    </Button>
                    <Button
                      variant="contained"
                      className="fr"
                      onClick={onClose}
                      color="secondary"
                    >
                      <FormattedMessage id="genericClose" />
                    </Button>
                  </div>
                )
              }
            </div>
          );
        }}
      />
    </FormGroup>
  );
};

export default UserFormGroup;
